import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, retry } from 'rxjs';
import { preparePostEscalationMenuItems } from '../../utils/getPostEscalationSelectionMenu';
import { Cookies, getCookie } from '../../utils/cookies';
import { getDefaultRequestHeaderParams } from '../../utils/getRequestHeaderParams';
import { getBrand } from '../../utils/getBrand';
import { toTitleCase } from '../../utils/toTitleCase';
import { getChatApiBaseUrl } from '../../utils/getChatApiBaseUrl';
import { Language } from '../types/enum';
import { getWindowUrl } from '../../utils/getWindowUrl';

@Injectable({
  providedIn: 'root',
})
export class ChatAdminApiService {
  constructor(private httpClient: HttpClient) {}

  callCustomerJourney({
    contextName,
    isFirstCall,
    language,
    sessionId,
  }): Observable<ChatAdminResponse> {
    return this.callChatAdminApi({
      contextName,
      isFirstCall,
      language,
      sessionId,
    }).pipe(
      map((response) => {
        return this.prepareChatAdminObject(response);
      })
    );
  }

  prepareChatAdminObject(response): ChatAdminResponse {
    const chatAdminresponse: ChatAdminResponse = {} as ChatAdminResponse;
    const data: any = response[0];
    chatAdminresponse.contextName = data.contextName;
    chatAdminresponse.escalationType = data.directLcEscalation;
    if (data.vaJourney) {
      chatAdminresponse.isVa = data.isVa;
      chatAdminresponse.initialQuery = data.vaJourney[0]['initialQuery'];
    }
    if (data.lcJourney) {
      const liveChatData = data.lcJourney[0];
      chatAdminresponse.directEscalation = {
        departmentId: liveChatData['departmentId'],
        platform: liveChatData['platform'],
        aaEligible: liveChatData['agentAssistFlag'],
      };
    }
    if (data.curation) {
      const curation = data.curation[0];
      const curationListEn = curation.curationListEn;
      const curationListFr = curation.curationListFr;
      chatAdminresponse.curationList = [
        {
          language: Language.en,
          list: preparePostEscalationMenuItems(curationListEn),
        },
        {
          language: Language.fr,
          list: preparePostEscalationMenuItems(curationListFr),
        },
      ];
    }
    if (data.banner) {
      const banner = data.banner;
      chatAdminresponse.alertConfig = {
        copy: {
          title: banner.bannerHeader,
          body: banner.bannerContent,
        },
        disableBorder: true,
        showCloseButton: true,
        variant: banner.bannerType,
        autoHideTiming: banner.autoHideTiming,
      };
      if (banner.bannerLink && banner.bannerLinkTarget) {
        chatAdminresponse.alertConfig.cta = [
          {
            copy: banner.bannerLink,
            href: banner.bannerLinkTarget,
            target: '_blank',
          },
        ];
      }
    }

    return chatAdminresponse;
  }

  getDefaultJourney(): Observable<ChatAdminResponse> {
    return this.httpClient
      .get(`configurations/default-journey/${getBrand().chatAdminBrand}.json`)
      .pipe(map((response) => this.prepareChatAdminObject(response)));
  }

  private callChatAdminApi({
    contextName,
    isFirstCall,
    language,
    sessionId,
  }): Observable<any> {
    const params = {
      brandName: toTitleCase(getBrand().chatAdminBrand),
    };
    if (contextName && contextName !== '') {
      params['contextName'] = contextName?.toUpperCase();
    } else {
      params['url'] = getWindowUrl();
    }
    // To get the Banner
    if (isFirstCall) {
      params['province'] = getCookie(Cookies.PROVINCE);
    }
    if (language && language !== '') {
      params['language'] = language?.toUpperCase();
    }
    return this.httpClient
      .get(getChatApiBaseUrl() + '/chatadmin/customerjourney', {
        headers: {
          ...getDefaultRequestHeaderParams({
            'session-identifier': sessionId,
          }),
        },
        params,
        withCredentials: true,
      })
      .pipe(retry(2));
  }
}
